import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router';

import {
  createProjectAuthorizer,
  deleteProjectAuthorizer,
  getProjectAuthorizer,
  getProjectAuthorizers,
  updateProjectAuthorizer,
} from '@/actions/projectAuthorizers';
import { useUser } from '@/providers/UserContext';

const ProjectAuthorizersContext = createContext(null);
const ProjectAuthorizersActionsContext = createContext(null);

function normalizeAuthorizerList(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && Array.isArray(data.authorizers)) {
    return data.authorizers;
  }
  return [];
}

function upsertAuthorizer(list, authorizer) {
  if (!authorizer?.id) {
    return list;
  }
  const existing = list.some((item) => item.id === authorizer.id);
  if (!existing) {
    return [...list, authorizer];
  }
  return list.map((item) =>
    item.id === authorizer.id ? { ...item, ...authorizer } : item,
  );
}

export function ProjectAuthorizersProvider({
  project_id: project_id_prop,
  children,
}) {
  const { id: route_project_id } = useParams();
  const user = useUser();
  const project_id = project_id_prop ?? route_project_id;
  const is_personal = Boolean(user?.id && project_id === user.id);
  const [authorizers, setAuthorizers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    if (!user?.id) {
      return;
    }
    if (!project_id || is_personal) {
      setAuthorizers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getProjectAuthorizers(project_id);
      setAuthorizers(normalizeAuthorizerList(data));
    } catch (err) {
      setError(err?.message || 'API Error');
      setAuthorizers([]);
    } finally {
      setLoading(false);
    }
  }, [project_id, is_personal, user?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loadAuthorizer = useCallback(
    async (authorizer_id) => {
      if (!project_id || !authorizer_id) {
        return null;
      }
      try {
        setError(null);
        return await getProjectAuthorizer(project_id, authorizer_id);
      } catch (err) {
        setError(err?.message || 'API Error');
        return null;
      }
    },
    [project_id],
  );

  const createAuthorizer = useCallback(
    async (values) => {
      try {
        setError(null);
        const created = await createProjectAuthorizer(project_id, values);
        if (created?.id) {
          setAuthorizers((prev) => upsertAuthorizer(prev || [], created));
        } else {
          const data = await getProjectAuthorizers(project_id);
          setAuthorizers(normalizeAuthorizerList(data));
        }
        return created;
      } catch (err) {
        setError(err?.message || 'API Error');
        throw err;
      }
    },
    [project_id],
  );

  const updateAuthorizer = useCallback(
    async (authorizer_id, values) => {
      try {
        setError(null);
        const updated = await updateProjectAuthorizer(
          project_id,
          authorizer_id,
          values,
        );
        const next_authorizer = {
          ...(updated || {}),
          id: updated?.id || authorizer_id,
        };
        setAuthorizers((prev) => upsertAuthorizer(prev || [], next_authorizer));
        return next_authorizer;
      } catch (err) {
        setError(err?.message || 'API Error');
        throw err;
      }
    },
    [project_id],
  );

  const removeAuthorizer = useCallback(
    async (authorizer_id) => {
      try {
        setError(null);
        await deleteProjectAuthorizer(project_id, authorizer_id);
        setAuthorizers((prev) =>
          (prev || []).filter((item) => item.id !== authorizer_id),
        );
      } catch (err) {
        setError(err?.message || 'API Error');
        throw err;
      }
    },
    [project_id],
  );

  const state_value = useMemo(
    () => ({
      authorizers,
      loading,
      error,
      project_id,
    }),
    [authorizers, loading, error, project_id],
  );

  const actions_value = useMemo(
    () => ({
      refresh,
      loadAuthorizer,
      createAuthorizer,
      updateAuthorizer,
      removeAuthorizer,
      clearError,
    }),
    [
      refresh,
      loadAuthorizer,
      createAuthorizer,
      updateAuthorizer,
      removeAuthorizer,
      clearError,
    ],
  );

  return (
    <ProjectAuthorizersContext.Provider value={state_value}>
      <ProjectAuthorizersActionsContext.Provider value={actions_value}>
        {children}
      </ProjectAuthorizersActionsContext.Provider>
    </ProjectAuthorizersContext.Provider>
  );
}

export function useProjectAuthorizers() {
  const context = useContext(ProjectAuthorizersContext);
  if (!context) {
    throw new Error(
      'useProjectAuthorizers must be used within ProjectAuthorizersProvider',
    );
  }
  return context;
}

export function useProjectAuthorizersActions() {
  const context = useContext(ProjectAuthorizersActionsContext);
  if (!context) {
    throw new Error(
      'useProjectAuthorizersActions must be used within ProjectAuthorizersProvider',
    );
  }
  return context;
}
